import { memo, useEffect, useRef } from 'react'
import { useSummariesNeedUpgrade } from '@renderer/chat/hooks/use-plan-features'
import { useAudioNoteSettings } from '@renderer/chat/hooks/useAudioNoteSettings'
import { useSavedNoteConfirmation } from '@renderer/hooks/audio/use-audio-note-selectors'
import { startSummaryGeneration } from '@renderer/hooks/audio/summary-generation'
import { useRendererOwnsSummaries } from '@renderer/hooks/audio/use-summary-generation'
import { SUMMARY_AUTOSTART_WINDOW_MS } from '@shared/audio-note-summary-ownership'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'

const log = GlobalLogger.bind('AudioNoteSummaryHandler')

/**
 * Starts the summary for each note the backend confirms saving, once. Lives at
 * the overlay root rather than in the Meeting View because saves that don't
 * open the view (shutdown, inactivity auto-stop, reconnect finalization) still
 * get a summary. The free and local-storage gates mirror main's legacy flow;
 * the route would answer 403 for the former anyway.
 */
export const AudioNoteSummaryHandler = memo(function AudioNoteSummaryHandler() {
  const enabled = useRendererOwnsSummaries()
  const confirmation = useSavedNoteConfirmation()
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const isFree = useSummariesNeedUpgrade()
  const startedNoteId = useRef<string | null>(null)

  useEffect(() => {
    if (!confirmation || startedNoteId.current === confirmation.noteId) {
      return
    }
    if (!enabled) {
      return
    }

    const confirmationAgeMs = Date.now() - confirmation.savedAt
    if (confirmationAgeMs > SUMMARY_AUTOSTART_WINDOW_MS) {
      log.warn(
        `Skipping renderer summary auto-start for audio note ${confirmation.noteId} (reason=stale-confirmation, ageMs=${confirmationAgeMs})`,
      )
      return
    }
    if (isUsingLocalStorage) {
      log.info(`Skipping renderer summary auto-start for audio note ${confirmation.noteId} (reason=local-storage)`)
      return
    }
    if (isFree) {
      log.info(`Skipping renderer summary auto-start for audio note ${confirmation.noteId} (reason=free-plan)`)
      return
    }
    startedNoteId.current = confirmation.noteId
    log.info(
      `Auto-starting renderer summary for audio note ${confirmation.noteId} (confirmationAgeMs=${confirmationAgeMs})`,
    )
    void startSummaryGeneration(confirmation.noteId, { trigger: 'auto-save' })
  }, [enabled, confirmation, isUsingLocalStorage, isFree])

  return null
})
